import { useEffect, useRef } from 'react';

/**
 * Branded confirmation dialog used for destructive actions (delete contact,
 * delete opportunity, etc.). Replaces window.confirm() with a styled,
 * accessible alertdialog that matches the app's visual language.
 *
 * Behaviour:
 *  - ESC closes the dialog (treated as cancel)
 *  - Clicking the backdrop closes (treated as cancel)
 *  - The Cancel button is focused on open — safer default for destructive
 *    flows than focusing the Delete button
 *  - role="alertdialog" + aria-labelledby + aria-describedby for SR users
 *  - When `variant="danger"`, the confirm button is signal-red and a small
 *    warning glyph appears next to the title
 */

export type ConfirmVariant = 'danger' | 'primary';

type Props = {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    // Focus the safer (cancel) button on open.
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const confirmClasses =
    variant === 'danger'
      ? 'inline-flex items-center justify-center min-h-touch px-5 py-2.5 rounded-card text-sm font-medium bg-signal text-white hover:bg-signal-600 disabled:opacity-60 transition-colors'
      : 'btn-primary disabled:opacity-60';

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-body"
      onMouseDown={(e) => {
        // Only treat clicks on the backdrop (this element directly) as cancel.
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
      className="fixed inset-0 z-[60] bg-graphite/50 backdrop-blur-[3px] flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md bg-surface rounded-card shadow-panel border border-platinum overflow-hidden">
        <header className="px-6 pt-5 pb-3 flex items-start gap-3">
          {variant === 'danger' && (
            <span
              aria-hidden="true"
              className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-bg-alert text-signal"
            >
              {/* Triangle warning glyph */}
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3l10 18H2L12 3z" />
                <path d="M12 10v5" />
                <path d="M12 18.5v.01" />
              </svg>
            </span>
          )}
          <div className="flex-1 min-w-0">
            <p className="label-uppercase text-ink-tertiary">
              — {variant === 'danger' ? 'Destructive action' : 'Confirm'}
            </p>
            <h2
              id="confirm-dialog-title"
              className="mt-1 font-display text-xl font-semibold text-graphite"
            >
              {title}
            </h2>
          </div>
        </header>

        <div
          id="confirm-dialog-body"
          className="px-6 pb-5 text-sm text-ink-secondary leading-relaxed"
        >
          {body}
        </div>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-platinum bg-glacier/40">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="btn-secondary disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={confirmClasses}
            data-autofocus="false"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
